import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Lock, AlertTriangle, Loader2, ChevronRight, Package, Layers } from "lucide-react";
import { getHeaders } from "@/lib/api";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: "En attente",
  in_progress: "En cours",
  ready: "Prête",
};

/**
 * Widget Dashboard : commandes imminentes dont certaines matières
 * (produits type) ne sont pas encore verrouillées.
 */
export default function MaterialsPendingLockWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/dashboard/materials-pending-lock`, { headers: getHeaders() })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card data-testid="materials-pending-lock-widget">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
          <Lock className="h-5 w-5 text-blue-700" />
          Matières à verrouiller
          {items.length > 0 && (
            <Badge className="bg-amber-100 text-amber-800" data-testid="pending-lock-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {items.length} commande(s)
            </Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground pt-1">
          Commandes imminentes (45j) avec des produits type non verrouillés —
          anticipe les passes commandes fournisseurs.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-2" data-testid="pending-lock-empty">
            Toutes les matières des commandes imminentes sont verrouillées.
          </p>
        ) : (
          <ul className="divide-y" data-testid="pending-lock-list">
            {items.slice(0, 10).map((it) => (
              <li
                key={it.order_id}
                className="flex items-center gap-2 py-2 text-sm hover:bg-slate-50 cursor-pointer px-1 -mx-1 rounded"
                onClick={() => navigate(`/commandes/${it.order_id}`)}
                data-testid={`pending-lock-row-${it.order_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-blue-700">
                      {it.order_reference || it.order_id.slice(0, 8)}
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      {STATUS_LABEL[it.order_status] || it.order_status}
                    </Badge>
                    {it.days_until !== null && it.days_until !== undefined && (
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded ${
                          it.days_until < 7 ? "bg-red-100 text-red-800"
                          : it.days_until < 21 ? "bg-amber-100 text-amber-800"
                          : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {it.days_until <= 0 ? "Démarrée" : `J-${it.days_until}`}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {it.client_name || "—"} · {it.missing_count}/{it.total_templates} matière(s) à verrouiller
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {it.missing.slice(0, 4).map((m) => (
                      <span
                        key={m.template_id}
                        className="inline-flex items-center gap-1 text-[10px] bg-slate-100 px-1.5 py-0.5 rounded"
                      >
                        {m.is_group
                          ? <Layers className="h-2.5 w-2.5 text-purple-600" />
                          : <Package className="h-2.5 w-2.5 text-blue-600" />}
                        {m.label}
                      </span>
                    ))}
                    {it.missing.length > 4 && (
                      <span className="text-[10px] text-slate-500">+{it.missing.length - 4}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </li>
            ))}
            {items.length > 10 && (
              <li className="pt-2 text-xs text-blue-700 hover:underline cursor-pointer" onClick={() => navigate("/commandes")}>
                + {items.length - 10} autres commandes — voir tout
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
